import { memo, useMemo, useRef, useCallback, StrictMode, useEffect, useState } from 'react';
import { useEvent } from '../../../../../../hooks/useEvent';
import { GridData, GridDataPagedResponse, GridHeaderRow, GridViewFilters, PagedRequestSortOptions } from '../../../../../../api/generated/data-contracts';
import { DragBox } from '../../../../_grid/components/DragBox';
import { AllRowsClosedProvider } from '../../../../_grid/contexts/allRowsClosedContext';
import { ExpandedGridProvider } from '../../../../_grid/contexts/expandedContext';
import { GridContext, GridContextProvider } from '../../../../_grid/contexts/gridContext';
import { UseGridProps, useGrid } from '../../../../_grid/useGrid';
import { useLocalStorage } from '../../../../../../hooks/useLocalStorage';
import { ScrollToContextProvider, ScrollToHandler } from '../context/ScrollToContext';
import { COLUMN_STICKY_COUNT, COLUMN_WIDTH, ROW_EXPANDED_HEIGHT, ROW_HEADER_WIDTH, ROW_HEIGHT, ROW_STICKY_COUNT, TOP_HEADERS_HEIGHT } from '../CONSTANTS';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { getRowType } from '../helpers/getRowType';
import { useDebouncedEventHandler } from '../../../../../../hooks/useDebouncedHandler';
import { useGridDragHandler } from '../hooks/useGridDragHandler';
import { DragContextProvider } from '../context/dragContext';
import { GridLayoutContextProvider, useGridLayoutContext } from '../context/LayoutContext';
import { HeaderContainer } from './HeaderContainer';
import { RowContainer } from './RowContainer/RowContainer';
import { useResourcePlannerData } from '../../../hooks/useResourcePlannerData';
import { useUnitType } from '../../../../../../Utilities/UnitTypeUtil';
import { ShimmerBox } from '../../../../../../_components/Skeleton/ShimmerBox';
import { Stack } from '@fluentui/react';
import shallow from 'zustand/shallow';
import { useStableVirtualItems } from '../../../../_grid/hooks/useStableVirtualItems';
import { InfiniteData } from '@tanstack/react-query';
import { RESOURCE_PLANNER } from '../../../../../../context/network/http/QueryProvider/queryKeys';
import { useMemoQueryKey } from '../../../../../../context/network/http/QueryProvider/queries/useMemoQueryKey';
import { useStore } from '../../../../../../context/store';
import produce from 'immer';
import { useUpdateOnUnmount } from '../../../../../../context/network/http/QueryProvider/queries/useUpdateOnUnmount';
import { SortContextProvider } from '../context/SortContext';

type ResourcePlannerGridProps = Omit<UseGridProps, 'data'> & { currentViewFilters: GridViewFilters };

const overscan = 5;
const loadingName = 'Loading..'

export const ResourcePlannerGrid = memo(function ResourcePlannerGrid({ currentViewFilters, readonly, ...rest }: ResourcePlannerGridProps) {
    const [chunkSize, setChunkSize] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { currentUnitType } = useUnitType();
    const { timelineStart, timelineEnd, timelineResolution } = useStore(store => {
        return {
            timelineEnd: store.timeline.end,
            timelineStart: store.timeline.start,
            timelineResolution: store.timeline.resolution,
        };
    }, shallow);

    const [sort, setSort] = useState<PagedRequestSortOptions | null>(null);

    const queryKey = useMemoQueryKey(RESOURCE_PLANNER, timelineStart, timelineEnd, timelineResolution, currentViewFilters, currentUnitType, sort);

    const {
        data: pagedData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useResourcePlannerData({
        currentViewFilters,
        chunkSize,
        overscan,
        queryKey,
        timelineEnd,
        timelineStart,
        timelineResolution,
        sort,
    });

    const [prevHeaders, setPrevHeaders] = useState<GridHeaderRow[]>([]);

    const data = useMemo((): GridData => {
        if (pagedData?.pages.length) {
            return {
                ...pagedData.pages[0].data,
                rows: pagedData.pages.flatMap(d => d.data.rows),
            };
        }
        return {
            headers: prevHeaders,
            id: '123',
            rows: [],
            metadata: null,
            name: loadingName,
        };
    }, [pagedData?.pages, prevHeaders]);

    const {
        gridData: { visibleRows, maxGridDepth, allClosed },
        gridMethods: {
            getRowAncestorTreeToRoot,
            getSubRows,
            updateCellValue,
            addRow,
            useGridCellState,
            useGridCellValue,
            getUpdatedData,
            moveRow,
            getRowsMap,
            setRootRows: setRows,
        },
        staticGridData: { headers, name, metadata, id },
        expandedRows,
        setExpandedRows,
    } = useGrid({
        ...rest,
        data,
    });

    useEffect(() => {
        if (headers) {
            setPrevHeaders(headers);
        }
    }, [headers]);

    useUpdateOnUnmount(queryKey, getUpdatedData, (newData, queryClient) => {
        const cachedData = queryClient.getQueryData<InfiniteData<GridDataPagedResponse> | void>(queryKey);
        if (cachedData) {
            const cellMap = getCellsMap(newData);
            const updatedData = produce(cachedData, cachedData => {
                cachedData.pages.forEach(({ data: { rows } }) => {
                    rows.forEach(row => {
                        row.columnRows.forEach(columnRow => {
                            columnRow.cells = columnRow.cells.map(cell => {
                                const newCell = cellMap.get(cell.id);
                                if (newCell) {
                                    return newCell;
                                }
                                return cell;
                            });
                        });
                    });
                });
            });
            queryClient.setQueryData<InfiniteData<GridDataPagedResponse>>(queryKey, () => updatedData);
        }
    });

    const context = useMemo((): GridContext => {
        return {
            name,
            headers,
            properties: metadata,
            id,
            readonly,
            maxGridDepth,
            updateCellValue,
            getRowAncestorTreeToRoot,
            addRow,
            useGridCellState,
            useGridCellValue,
            moveRow,
            getRowsMap,
            setRootRows: setRows,
        };
    }, [
        name,
        headers,
        metadata,
        id,
        readonly,
        maxGridDepth,
        updateCellValue,
        getRowAncestorTreeToRoot,
        addRow,
        useGridCellState,
        useGridCellValue,
        moveRow,
        getRowsMap,
        setRows,
    ]);

    const expandedContext = useMemo(() => {
        return {
            expandedRows,
            setExpandedRows,
        };
    }, [expandedRows, setExpandedRows]);

    const renderableRows = useMemo(() => {
        return visibleRows.filter(({ visible }) => visible);
    }, [visibleRows]);

    const activeColumnStickyIndexRef = useRef(0);
    const activeRowStickyIndexRef = useRef(0);

    const stickyColumnIndexes = useMemo(() => [0], []);
    const stickyRowIndexes = useMemo(() => [0], []);

    const pendingCellIndexScroll = useRef<{ dir: 'x' | 'y'; index: number } | null>(null);

    const [rowHeaderSize, _setRowHeaderSize] = useLocalStorage('row-header-width', ROW_HEADER_WIDTH);

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: (visibleRows?.[0]?.row?.columnRows?.length || prevHeaders?.length || 0) + COLUMN_STICKY_COUNT,
        getScrollElement: () => scrollRef.current,
        overscan,
        estimateSize: useCallback(
            i => {
                // First columns is the row headers
                if (i === 0) {
                    return rowHeaderSize;
                }
                return COLUMN_WIDTH;
            },
            [rowHeaderSize],
        ),
        rangeExtractor: useCallback(
            range => {
                activeColumnStickyIndexRef.current = [...stickyColumnIndexes].reverse().find(index => range.startIndex >= index);

                const next = new Set([activeColumnStickyIndexRef.current, ...defaultRangeExtractor(range)]);

                return [...next].sort((a, b) => a - b);
            },
            [stickyColumnIndexes],
        ),
        onChange: useEvent((instance, sync) => {
            if (!sync) {
                if (pendingCellIndexScroll.current !== null) {
                    const { dir, index } = pendingCellIndexScroll.current;
                    if (dir === 'y') {
                        rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
                        pendingCellIndexScroll.current = null;
                    }
                }
            }
        }),
    });

    const rowCount = useMemo(() => {
        if (!pagedData) {
            return ROW_STICKY_COUNT + chunkSize;
        }
        if (hasNextPage) {
            return renderableRows.length + ROW_STICKY_COUNT + 1;
        }
        return renderableRows.length + ROW_STICKY_COUNT;
    }, [chunkSize, hasNextPage, pagedData, renderableRows.length]);

    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        overscan,
        estimateSize: useCallback(
            (index: number) => {
                if (index === 0) {
                    return TOP_HEADERS_HEIGHT;
                }

                const visibleRow = renderableRows[index - ROW_STICKY_COUNT];
                if (!visibleRow) {
                    return ROW_HEIGHT;
                }
                const { expanded, row } = visibleRow;
                if (expanded) {
                    const rowType = getRowType(row);
                    switch (rowType) {
                        case 'resource':
                            return ROW_EXPANDED_HEIGHT;

                        case 'project':
                        case 'contract':
                        default:
                            return ROW_HEIGHT;
                    }
                }
                return ROW_HEIGHT;
            },
            [renderableRows],
        ),
        rangeExtractor: useCallback(
            range => {
                activeRowStickyIndexRef.current = [...stickyRowIndexes].reverse().find(index => range.startIndex >= index);

                const next = new Set([activeRowStickyIndexRef.current, ...defaultRangeExtractor(range)]);

                return [...next].sort((a, b) => a - b);
            },
            [stickyRowIndexes],
        ),
        onChange: useEvent((instance, sync) => {
            if (!sync) {
                if (pendingCellIndexScroll.current !== null) {
                    const { dir, index } = pendingCellIndexScroll.current;
                    if (dir === 'x') {
                        columnVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
                        pendingCellIndexScroll.current = null;
                    }
                }
            }
        }),
    });

    useEffect(() => {
        if (rowVirtualizer.scrollRect?.height && chunkSize === 0) {
            setChunkSize(Math.floor(rowVirtualizer.scrollRect.height / ROW_HEIGHT) || 10);
        }
    }, [rowVirtualizer.scrollRect?.height, chunkSize]);

    const rows = useStableVirtualItems(rowVirtualizer.getVirtualItems());

    useEffect(() => {
        if (!pagedData) {
            return;
        }

        const lastItem = rows.at(-1);

        if (!lastItem) {
            return;
        }

        if (renderableRows.length && lastItem.index >= renderableRows.length - 3 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, renderableRows.length, isFetchingNextPage, rows, pagedData]);

    const setRowHeaderSize = useDebouncedEventHandler<React.Dispatch<React.SetStateAction<number>>>(
        () => {
            columnVirtualizer.measure();
        },
        arg => {
            _setRowHeaderSize(arg);
        },
        20,
    );

    const layoutValue = useMemo(() => ({ rowHeaderSize, setRowHeaderSize }), [rowHeaderSize, setRowHeaderSize]);

    const scrollTo = useDebouncedEventHandler<ScrollToHandler>(({ x, y, rowIndex, columnIndex, behavior = 'smooth', align = 'center' }) => {
        const scrollToIndex = (columnIndex?: number, rowIndex?: number) => {
            if (columnIndex !== undefined && rowIndex !== undefined) {
                rowVirtualizer.scrollToIndex(rowIndex, { align, behavior });
                pendingCellIndexScroll.current = { dir: 'x', index: columnIndex };
            }
            if (columnIndex !== undefined) {
                columnVirtualizer.scrollToIndex(columnIndex, { align, behavior });
            }
            if (rowIndex !== undefined) {
                rowVirtualizer.scrollToIndex(rowIndex, { align, behavior });
            }
        };
        if (x !== undefined && y !== undefined) {
            const colIndex = columnVirtualizer.getVirtualItemForOffset(x).index;
            const rowIndex = rowVirtualizer.getVirtualItemForOffset(y).index;
            scrollToIndex(colIndex, rowIndex);
            return;
        }
        if (x !== undefined) {
            const colIndex = columnVirtualizer.getVirtualItemForOffset(x).index;
            scrollToIndex(colIndex);
            return;
        }
        if (y !== undefined) {
            const rowIndex = rowVirtualizer.getVirtualItemForOffset(x).index;
            scrollToIndex(undefined, rowIndex);
            return;
        }
        scrollToIndex(columnIndex, rowIndex);
    }, 20);

    const columns = useStableVirtualItems(columnVirtualizer.getVirtualItems());
    const totalWidth = columnVirtualizer.getTotalSize();
    const totalHeight = rowVirtualizer.getTotalSize();

    const renderedRows = useMemo(() => {
        return rows.map(({ index, start, key }) => {
            if (index === 0) {
                return <HeaderContainer key={String(key)} headers={headers} start={start} totalWidth={totalWidth} columns={columns} />;
            }
            const rowIndex = index - ROW_STICKY_COUNT;
            const visibleRow = renderableRows[rowIndex];
            if (!visibleRow) {
                return <ShimmerRow key={String(key)} start={start} totalWidth={totalWidth} columnCount={headers.length} />;
            }
            const { row, visible, nestingLevel, expanded, rowRelations } = visibleRow;
            return (
                <RowContainer
                    key={String(key)}
                    row={row}
                    setExpandedRows={setExpandedRows}
                    visible={visible}
                    expanded={expanded}
                    getRowAncestorTreeToRoot={getRowAncestorTreeToRoot}
                    getSubRows={getSubRows}
                    nestingLevel={nestingLevel}
                    maxGridDepth={maxGridDepth}
                    rowIndex={rowIndex}
                    rowRelations={rowRelations}
                    start={start}
                    columns={columns}
                    totalWidth={totalWidth}
                />
            );
        });
    }, [columns, getRowAncestorTreeToRoot, getSubRows, headers, maxGridDepth, renderableRows, rows, setExpandedRows, totalWidth]);

    const dragContext = useGridDragHandler({
        getVirtualColumnItemForOffset: useEvent(columnVirtualizer.getVirtualItemForOffset),
        getVirtualRowItemForOffset: useEvent(rowVirtualizer.getVirtualItemForOffset),
        columnScrollOffset: columnVirtualizer.scrollOffset,
        rowScrollOffset: rowVirtualizer.scrollOffset,
        renderableRows,
        scrollRef,
        updateCellValue,
    });

    const innerScrollContainerStyles = useMemo(
        (): React.CSSProperties => ({
            position: 'relative',
            width: '100%',
            height: `${totalHeight}px`,
        }),
        [totalHeight],
    );

    return (
        <StrictMode>
            <SortContextProvider setSort={setSort} sort={sort}>
                <ScrollToContextProvider value={scrollTo}>
                    <GridLayoutContextProvider value={layoutValue}>
                        <DragContextProvider value={dragContext}>
                            <GridContextProvider value={context}>
                                <ExpandedGridProvider value={expandedContext}>
                                    <AllRowsClosedProvider value={allClosed}>
                                        <div ref={scrollRef} style={scrollContainerStyle}>
                                            <div style={innerScrollContainerStyles}>{renderedRows}</div>
                                        </div>
                                        <DragBox />
                                    </AllRowsClosedProvider>
                                </ExpandedGridProvider>
                            </GridContextProvider>
                        </DragContextProvider>
                    </GridLayoutContextProvider>
                </ScrollToContextProvider>
            </SortContextProvider>
        </StrictMode>
    );
});

const scrollContainerStyle: React.CSSProperties = {
    height: `100%`,
    width: `100%`,
    overflow: 'auto',
    maxWidth: '100%',
};

const ShimmerRow = ({ start, totalWidth, columnCount }: { start: number; totalWidth: number; columnCount: number }) => {
    const { rowHeaderSize } = useGridLayoutContext();
    const style = useMemo((): React.CSSProperties => {
        return {
            position: 'absolute',
            transform: `translateY(${start}px)`,
            top: 0,
            left: 0,
            height: `${ROW_HEIGHT}px`,
            width: `${totalWidth}px`,
            boxSizing: 'border-box',
            display: 'flex',
        };
    }, [start, totalWidth]);
    const columns = useMemo(() => {
        return Array.from({ length: columnCount }, (_, i) => {
            return (
                <Stack key={i} verticalAlign="center" horizontalAlign="center" styles={{ root: { width: COLUMN_WIDTH } }}>
                    <ShimmerBox height={ROW_HEIGHT - 10} width={COLUMN_WIDTH - 10} />
                </Stack>
            );
        });
    }, [columnCount]);
    return (
        <div style={style}>
            <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { width: rowHeaderSize } }}>
                <ShimmerBox height={ROW_HEIGHT - 10} width={rowHeaderSize - 10} />
            </Stack>
            {columns}
        </div>
    );
};

const getCellsMap = (data: GridData) => {
    const allCells = data.rows.flatMap(row => row.columnRows.flatMap(columnRow => columnRow.cells));
    return new Map(allCells.map(cell => [cell.id, cell]));
};
